// 品質管理報告書 (発注者提出系)
// 工事ヘッダ部 + 温湿度測定結果一覧 + 含水率・膜厚 + 規格値一覧。

import ExcelJS from "exceljs";
import type { ExtendedDB } from "../../../../types/extendedDB";
import { deriveQualityMeasurements, type DerivedQualityMeasurement } from "../../../../types/derivedSelectors";
import { buildLookups, downloadBlob, todayStr, setHeaderRow, judgeChar, fmtDateTime, round1, nullToDash } from "./helpers";
import type { Lookups } from "./helpers";

type WorkRecord = ReturnType<Lookups["workById"]["get"]>;
type QmItem = ExtendedDB["qualityMeasurementItems"][number];

function resolveWork(db: ExtendedDB, lk: Lookups, workId?: number): WorkRecord {
  if (workId != null) {
    return lk.workById.get(workId);
  }
  return db.works.find((w) => w.deleted_at == null);
}

// q.construction_id → construction.construction_plan_id → constructionPlanAreas[].work_id を
// 辿ることで、計測値がどの工事に属するかを判定する。
// 構築前に Set にしておくことで O(1) 検索とする。
function buildConstructionIdsForWork(
  db: Pick<ExtendedDB, "constructions" | "constructionPlanAreas">,
  work: WorkRecord,
): Set<string> {
  const result = new Set<string>();
  if (!work) {
    return result;
  }
  const planIdsForWork = new Set<string>();
  for (const area of db.constructionPlanAreas) {
    if (area.deleted_at != null) {
      continue;
    }
    if (area.work_id === work.id) {
      planIdsForWork.add(area.construction_plan_id);
    }
  }
  for (const con of db.constructions) {
    if (con.deleted_at != null) {
      continue;
    }
    if (planIdsForWork.has(con.construction_plan_id)) {
      result.add(con.id);
    }
  }
  return result;
}

function isMeasurementInWork(q: DerivedQualityMeasurement, work: WorkRecord, constructionIdsForWork: Set<string>): boolean {
  if (!work) {
    return true;
  }
  if (q.construction_id == null) {
    return false;
  }
  return constructionIdsForWork.has(q.construction_id);
}

function collectRelevantMaterialIds(db: ExtendedDB, lk: Lookups, work: WorkRecord): Set<number> {
  const relevantMaterialIds = new Set<number>();
  if (!work) {
    return relevantMaterialIds;
  }
  for (const c of db.carryIns.filter((c) => c.deleted_at == null)) {
    if (c.work_id !== work.id) continue;
    const qrs = db.materialQrs.filter((q) => q.carry_in_id === c.id);
    for (const q of qrs) {
      if (!q.lot_id) continue;
      const lot = lk.lotById.get(q.lot_id);
      if (lot && lot.material_id != null) {
        relevantMaterialIds.add(lot.material_id);
      }
    }
  }
  for (const area of db.constructionPlanAreas.filter((a) => a.deleted_at == null)) {
    if (area.work_id !== work.id) continue;
    const wds = db.workDetails.filter((d) => d.deleted_at == null && d.work_subtype_id === area.work_subtype_id);
    for (const wd of wds) {
      if (wd.mix_ratio_definition_id != null) {
        const def = lk.mixRatioDefById.get(wd.mix_ratio_definition_id);
        if (def) {
          relevantMaterialIds.add(def.material_id);
        }
      }
    }
  }
  return relevantMaterialIds;
}

function isMaterialInWork(materialId: number, work: WorkRecord, relevantMaterialIds: Set<number>): boolean {
  if (!work || relevantMaterialIds.size === 0) {
    return true;
  }
  return relevantMaterialIds.has(materialId);
}

function headerRowFor(work: WorkRecord): number {
  if (work) {
    return 8;
  }
  return 1;
}

function materialNameOf(materialId: number | string | null | undefined, lk: Lookups): string {
  if (materialId) {
    return lk.materialById.get(Number(materialId))?.name ?? "";
  }
  return "";
}

function lookupOpt<V>(map: Map<number, V>, id: number | null | undefined): V | undefined {
  if (id != null) {
    return map.get(id);
  }
  return undefined;
}

function resolveScopeTarget(item: QmItem, lk: Lookups): string {
  if (item.scope_kind === "material") {
    return lk.materialById.get(Number(item.material_id))?.name ?? "";
  }
  const wd = lookupOpt(lk.workDetailById, item.work_detail_id);
  const wst = lookupOpt(lk.workSubtypeById, resolveWorkSubtypeId(item, wd));
  const wt = lookupOpt(lk.workTypeById, resolveWorkTypeId(item, wst));
  return `工種:${wt?.name ?? "すべて"} / 種別:${wst?.name ?? "すべて"} / 工程:${wd?.name ?? "すべて"}`;
}

function resolveWorkSubtypeId(item: QmItem, wd: ReturnType<Lookups["workDetailById"]["get"]>): number | undefined {
  if (item.work_subtype_id != null) return item.work_subtype_id;
  if (wd?.work_subtype_id != null) return wd.work_subtype_id;
  return undefined;
}

function resolveWorkTypeId(item: QmItem, wst: ReturnType<Lookups["workSubtypeById"]["get"]>): number | undefined {
  if (item.work_type_id != null) return item.work_type_id;
  if (wst?.work_type_id != null) return wst.work_type_id;
  return undefined;
}

export async function exportQualityControlReport(db: ExtendedDB, workId?: number): Promise<void> {
  const lk = buildLookups(db);
  const work = resolveWork(db, lk, workId);
  const qualityMeasurements = deriveQualityMeasurements(db);
  const constructionIdsForWork = buildConstructionIdsForWork(db, work);

  const wb = new ExcelJS.Workbook();
  wb.creator = "奥村組 トレーサビリティシステム";

  const ws1 = wb.addWorksheet("温湿度測定結果一覧");
  if (work) {
    ws1.getCell("A1").value = "品質管理報告書 — 温湿度測定結果一覧";
    ws1.getCell("A1").font = { bold: true, size: 14 };
    ws1.getCell("A2").value = "工事名:";
    ws1.getCell("A2").font = { bold: true };
    ws1.getCell("B2").value = work.official_name;
    ws1.getCell("A3").value = "整理番号:";
    ws1.getCell("A3").font = { bold: true };
    ws1.getCell("B3").value = work.control_number;
    ws1.getCell("A4").value = "工期:";
    ws1.getCell("A4").font = { bold: true };
    ws1.getCell("B4").value = `${work.period_start} 〜 ${work.period_end}`;
    ws1.getCell("A5").value = "受注者:";
    ws1.getCell("A5").font = { bold: true };
    ws1.getCell("B5").value = work.contractor_name;
    ws1.getCell("A6").value = "出力日:";
    ws1.getCell("A6").font = { bold: true };
    ws1.getCell("B6").value = todayStr();
  }
  const headerRow = headerRowFor(work);
  ws1.getCell(headerRow, 1).value = "測定日時";
  ws1.getCell(headerRow, 2).value = "種別";
  ws1.getCell(headerRow, 3).value = "材料";
  ws1.getCell(headerRow, 4).value = "場所";
  ws1.getCell(headerRow, 5).value = "温度(℃)";
  ws1.getCell(headerRow, 6).value = "湿度(%)";
  ws1.getCell(headerRow, 7).value = "合否";
  ws1.getCell(headerRow, 8).value = "備考";
  [20, 14, 30, 40, 10, 10, 8, 30].forEach((w, i) => { ws1.getColumn(i + 1).width = w; });
  setHeaderRow(ws1, headerRow);
  const tempHumidRows = qualityMeasurements
    .filter((q) => q.measurement_kind === "carry_in_temp_humid" || q.measurement_kind === "construction_temp_humid")
    .filter((q) => isMeasurementInWork(q, work, constructionIdsForWork))
    .sort((a, b) => a.measured_at.localeCompare(b.measured_at));
  let rowIdx = headerRow + 1;
  for (const q of tempHumidRows) {
    const matName = materialNameOf(q.material_id, lk);
    ws1.getCell(rowIdx, 1).value = fmtDateTime(q.measured_at);
    ws1.getCell(rowIdx, 2).value = q.measurement_kind === "carry_in_temp_humid" ? "搬入時" : "施工時";
    ws1.getCell(rowIdx, 3).value = matName;
    ws1.getCell(rowIdx, 4).value = q.location_label ?? "";
    ws1.getCell(rowIdx, 5).value = round1(q.temperature);
    ws1.getCell(rowIdx, 6).value = round1(q.humidity);
    ws1.getCell(rowIdx, 7).value = judgeChar(q.judgment);
    ws1.getCell(rowIdx, 8).value = q.remarks ?? "";
    rowIdx++;
  }

  const ws2 = wb.addWorksheet("含水率・膜厚");
  ws2.getCell("A1").value = "含水率・膜厚測定結果一覧";
  ws2.getCell("A1").font = { bold: true, size: 14 };
  const ws2HeaderRow = 3;
  ws2.getCell(ws2HeaderRow, 1).value = "測定日時";
  ws2.getCell(ws2HeaderRow, 2).value = "種別";
  ws2.getCell(ws2HeaderRow, 3).value = "材料";
  ws2.getCell(ws2HeaderRow, 4).value = "施工箇所";
  ws2.getCell(ws2HeaderRow, 5).value = "タイミング";
  ws2.getCell(ws2HeaderRow, 6).value = "測定値";
  ws2.getCell(ws2HeaderRow, 7).value = "単位";
  ws2.getCell(ws2HeaderRow, 8).value = "合否";
  ws2.getCell(ws2HeaderRow, 9).value = "備考";
  [20, 14, 30, 30, 20, 12, 6, 8, 30].forEach((w, i) => { ws2.getColumn(i + 1).width = w; });
  setHeaderRow(ws2, ws2HeaderRow);
  const mtRows = qualityMeasurements
    .filter((q) => q.measurement_kind === "moisture" || q.measurement_kind === "thickness")
    .filter((q) => isMeasurementInWork(q, work, constructionIdsForWork))
    .sort((a, b) => a.measured_at.localeCompare(b.measured_at));
  let r2 = ws2HeaderRow + 1;
  for (const mq of mtRows) {
    const matName2 = materialNameOf(mq.material_id, lk);
    ws2.getCell(r2, 1).value = fmtDateTime(mq.measured_at);
    ws2.getCell(r2, 2).value = mq.measurement_kind === "moisture" ? "表面含水率" : "膜厚";
    ws2.getCell(r2, 3).value = matName2;
    ws2.getCell(r2, 4).value = mq.location_label ?? "";
    ws2.getCell(r2, 5).value = mq.timing_category ?? "";
    ws2.getCell(r2, 6).value = mq.measurement_kind === "moisture" ? round1(mq.moisture_pct) : round1(mq.thickness_um);
    ws2.getCell(r2, 7).value = mq.measurement_kind === "moisture" ? "%" : "μ";
    ws2.getCell(r2, 8).value = judgeChar(mq.judgment);
    ws2.getCell(r2, 9).value = mq.remarks ?? "";
    r2++;
  }

  const ws3 = wb.addWorksheet("規格値一覧");
  ws3.getCell("A1").value = "規格値一覧";
  ws3.getCell("A1").font = { bold: true, size: 14 };
  const ws3HeaderRow = 3;
  ws3.getCell(ws3HeaderRow, 1).value = "材料 / スコープ";
  ws3.getCell(ws3HeaderRow, 2).value = "種別";
  ws3.getCell(ws3HeaderRow, 3).value = "下限";
  ws3.getCell(ws3HeaderRow, 4).value = "上限";
  [40, 30, 10, 10].forEach((w, i) => { ws3.getColumn(i + 1).width = w; });
  setHeaderRow(ws3, ws3HeaderRow);
  const relevantMaterialIds = collectRelevantMaterialIds(db, lk, work);
  let r3 = ws3HeaderRow + 1;
  for (const m of db.materials) {
    if (m.deleted_at != null) continue;
    if (!isMaterialInWork(m.id, work, relevantMaterialIds)) continue;
    if (m.humidity_upper != null || m.humidity_lower != null) {
      ws3.getCell(r3, 1).value = m.name;
      ws3.getCell(r3, 2).value = "湿度(%)";
      ws3.getCell(r3, 3).value = nullToDash(m.humidity_lower);
      ws3.getCell(r3, 4).value = nullToDash(m.humidity_upper);
      r3++;
    }
    if (m.standard_usage != null && m.standard_usage > 0 && m.standard_usage_unit) {
      ws3.getCell(r3, 1).value = m.name;
      ws3.getCell(r3, 2).value = `標準使用量(${m.standard_usage_unit})`;
      ws3.getCell(r3, 3).value = m.standard_usage;
      ws3.getCell(r3, 4).value = m.standard_usage;
      r3++;
    }
  }
  for (const s of db.potLifeSpecs) {
    if (s.deleted_at != null) continue;
    if (!isMaterialInWork(s.material_id, work, relevantMaterialIds)) continue;
    const matName3 = lk.materialById.get(s.material_id)?.name ?? "";
    const range = `温度 ${s.temperature_lower ?? "-∞"}〜${s.temperature_upper ?? "+∞"}℃`;
    ws3.getCell(r3, 1).value = matName3;
    ws3.getCell(r3, 2).value = `可使時間 / ${s.definition_name} / ${range}`;
    ws3.getCell(r3, 3).value = "";
    ws3.getCell(r3, 4).value = s.pot_life_minutes ?? "制限なし";
    r3++;
  }
  for (const item of db.qualityMeasurementItems) {
    if (item.deleted_at != null) continue;
    if (item.scope_kind === "material" && item.material_id != null
      && !isMaterialInWork(Number(item.material_id), work, relevantMaterialIds)) continue;
    const target = resolveScopeTarget(item, lk);
    ws3.getCell(r3, 1).value = target;
    ws3.getCell(r3, 2).value = `${item.name}(${item.unit})`;
    ws3.getCell(r3, 3).value = nullToDash(item.lower_limit);
    ws3.getCell(r3, 4).value = nullToDash(item.upper_limit);
    r3++;
  }

  const buffer = await wb.xlsx.writeBuffer();
  downloadBlob(buffer, `品質管理報告書_${todayStr()}.xlsx`);
}
